import { useState, useMemo } from 'react';

import { pdf } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

import useEnv from '@zenbusiness/application-commons-env';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';
import { useCustomForm } from '#client/contexts/CustomFormContext';

import * as Styled from './BusinessEntityAdvisorV2Results.style';
import PoweredByVelo from '../PoweredByVelo/PoweredByVelo';
import { ReportPanel } from '../ReportLayout/ReportPanel';
import SelectionPanel from '../ReportLayout/SelectionPanel';
import SidePanel from '../ReportLayout/SidePanel';
import { BUSINESS_ENTITY_ADVISOR_SECTIONS } from '../utils/constants';
import { transformBusinessEntityAdvisorSection } from '../utils/reportUtils';
import { BusinessEntityAdvisorV2ResultPDF } from './components/BusinessEntityAdvisorV2ResultPDF/BusinessEntityAdvisorV2ResultPDF';
import { BusinessEntityAdvisorV2ResultsData } from './types';

/**
 * Business Entity Advisor V2 Results component
 * Displays personalized business entity recommendation and detailed analysis
 * Uses shared ReportPanel for section rendering
 *
 * @param {object} props - Component props
 * @param {object} props.data - Business entity advisor data containing sections
 * @param {string[]} [props.actions] - Enabled actions: 'download_pdf', 'start_over'
 * @param {Function} [props.onComponentClick] - Callback for component actions (follows assistant-ui pattern)
 * @param {object} props.report - Report metadata (title, description)
 * @component
 */
function BusinessEntityAdvisorV2Results({ data, actions = [], report, onComponentClick }) {
  const { DEPLOY_ENV } = useEnv('head-first');
  const { isLoading, setLoading, resetForm, clearAnswers } = useCustomForm();

  const enabledActions = Array.isArray(actions) ? actions : [];
  const canDownloadPdf = enabledActions.includes('download_pdf');
  const canStartOver = enabledActions.includes('start_over');

  // Transform sections into items array for navigation
  // Each main section becomes a selectable item in the sidebar
  // Transform the nested Business Entity Advisor format to ReportPanel's Format 3
  const items = useMemo(() => {
    if (!data.sections) return [];

    return Object.entries(data.sections).map(([sectionKey, sectionData]) => ({
      id: sectionKey,
      title: sectionData.title || sectionKey,
      description: '',
      // Transform the nested content structure to Format 3 (sections object)
      sections: transformBusinessEntityAdvisorSection(sectionData)
    }));
  }, [data.sections]);

  const [selectedItemId, setSelectedItemId] = useState(items[0]?.id);

  // Find the currently selected section
  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedItemId),
    [items, selectedItemId]
  );

  const handleItemSelect = (itemId) => {
    setSelectedItemId(itemId);
  };

  const handleDownloadReport = async () => {
    if (isLoading) return;

    setLoading(true);

    try {
      const blob = await pdf(
        <BusinessEntityAdvisorV2ResultPDF environment={DEPLOY_ENV} report={data} />
      ).toBlob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.title.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[BusinessEntityAdvisorV2Results] Download report error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOver = async () => {
    if (isLoading) return;

    setLoading(true);
    if (onComponentClick) {
      await resetForm();
      await clearAnswers();
      await onComponentClick({ type: 'business_results_start_over' });
    }
    setLoading(false);
  };

  // Handle case where no sections are provided
  if (!items || items.length === 0) {
    return (
      <Styled.BusinessEntityAdvisorV2ResultsRoot>
        <Box style={{ textAlign: 'center' }}>
          <Text>No recommendation found</Text>
          <Text>
            We weren&apos;t able to generate a recommendation based on your responses. Please try
            again.
          </Text>
        </Box>
      </Styled.BusinessEntityAdvisorV2ResultsRoot>
    );
  }

  // Find the section config for the selected section
  const sectionConfig = BUSINESS_ENTITY_ADVISOR_SECTIONS.find(
    (section) => section.id === selectedItemId
  );

  return (
    <Styled.BusinessEntityAdvisorV2ResultsRoot>
      <Styled.ReportHeader>
        <Text size="md" weight="medium">
          {report.title}
        </Text>
        {report.description && <Text size="sm">{report.description}</Text>}
      </Styled.ReportHeader>
      <Styled.ReportContent>
        <SidePanel
          selectionPanel={
            <SelectionPanel
              clickstreamPrefix="business_entity_advisor"
              getDisplayTitle={(item) => item.title}
              getItemId={(item) => item.id}
              items={items}
              loading={isLoading}
              selectedItemId={selectedItemId}
              startOverText="Start over"
              onItemSelect={handleItemSelect}
              onStartOver={canStartOver ? handleStartOver : undefined}
            />
          }
        />

        <ReportPanel
          iconVariant="rounded"
          loading={isLoading}
          reportData={selectedItem}
          sectionsConfig={sectionConfig?.subsections}
          onDownloadReport={canDownloadPdf ? handleDownloadReport : undefined}
        />
      </Styled.ReportContent>
      <PoweredByVelo url={ROUTES.website(DEPLOY_ENV)} />
    </Styled.BusinessEntityAdvisorV2ResultsRoot>
  );
}

BusinessEntityAdvisorV2Results.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.string),
  data: BusinessEntityAdvisorV2ResultsData,
  onComponentClick: PropTypes.func,
  report: PropTypes.object
};

export default BusinessEntityAdvisorV2Results;
